import React, { useState, useEffect, useCallback, useRef } from 'react';
import { EnvelopeIcon, FunnelIcon, MagnifyingGlassIcon, XMarkIcon, ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { NotificationLogService } from '../../../shared/services/notificationLogService';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const INTERVAL_OPTIONS = [
  { value: '', label: 'All Intervals' },
  { value: '7d', label: '1 Week Before' },
  { value: '3d', label: '3 Days Before' },
  { value: '1d', label: '1 Day Before' },
];

const PAGE_SIZE = 20;

// ── Badge helpers ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    sent: 'bg-green-100 text-green-700 border border-green-200',
    failed: 'bg-red-100 text-red-700 border border-red-200',
    skipped: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
  };
  const label = { sent: 'Sent', failed: 'Failed', skipped: 'Skipped' };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'sent' ? 'bg-green-500' : status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
      {label[status] ?? status}
    </span>
  );
};

const IntervalBadge = ({ type }) => {
  const map = {
    '7d': { cls: 'bg-blue-100 text-blue-700 border border-blue-200', label: '1 Week' },
    '3d': { cls: 'bg-purple-100 text-purple-700 border border-purple-200', label: '3 Days' },
    '1d': { cls: 'bg-orange-100 text-orange-700 border border-orange-200', label: '1 Day' },
  };
  const { cls, label } = map[type] ?? { cls: 'bg-gray-100 text-gray-600 border border-gray-200', label: type };
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
};

// ── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, colorClass, loading }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold ${colorClass}`}>
      {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : value}
    </div>
    <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
  </div>
);

// ── Format helpers ────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const formatScheduledDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function NotificationLogsPage() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    intervalType: '',
    startDate: '',
    endDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const searchDebounceRef = useRef(null);

  // ── Data fetching ───────────────────────────────────────────────────────────

  const fetchLogs = useCallback(async (page, activeFilters) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: PAGE_SIZE, ...activeFilters };
      // strip empty strings
      Object.keys(params).forEach((k) => { if (params[k] === '') delete params[k]; });

      const res = await NotificationLogService.getAll(params);
      setLogs(res.data.logs ?? []);
      setPagination(res.data.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load notification logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await NotificationLogService.getStats();
      setStats(res.data.data);
    } catch {
      // stats are non-critical
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    fetchLogs(currentPage, appliedFilters);
  }, [currentPage, appliedFilters, fetchLogs]);

  // ── Search debounce ─────────────────────────────────────────────────────────

  const handleSearchChange = (value) => {
    setFilters((f) => ({ ...f, search: value }));
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setAppliedFilters((f) => ({ ...f, search: value }));
    }, 400);
  };

  // ── Filter actions ──────────────────────────────────────────────────────────

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...filters });
    setShowFilters(false);
  };

  const clearFilters = () => {
    const empty = { search: '', status: '', intervalType: '', startDate: '', endDate: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const activeFilterCount = [
    appliedFilters.status,
    appliedFilters.intervalType,
    appliedFilters.startDate,
    appliedFilters.endDate,
  ].filter(Boolean).length;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-lg bg-[#4057fe] flex items-center justify-center">
            <EnvelopeIcon className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Email Notification Logs</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 ml-12">
          Track vaccination reminder emails sent to parents
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={stats?.total ?? '—'} colorClass="bg-[#4057fe]" loading={statsLoading} />
        <StatCard label="Sent" value={stats?.sent ?? '—'} colorClass="bg-green-500" loading={statsLoading} />
        <StatCard label="Failed" value={stats?.failed ?? '—'} colorClass="bg-red-500" loading={statsLoading} />
        <StatCard label="Skipped" value={stats?.skipped ?? '—'} colorClass="bg-yellow-500" loading={statsLoading} />
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by email or child name..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30 focus:border-[#4057fe]"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Quick status filter */}
            <select
              value={appliedFilters.status}
              onChange={(e) => { setAppliedFilters((f) => ({ ...f, status: e.target.value })); setCurrentPage(1); }}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Quick interval filter */}
            <select
              value={appliedFilters.intervalType}
              onChange={(e) => { setAppliedFilters((f) => ({ ...f, intervalType: e.target.value })); setCurrentPage(1); }}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
            >
              {INTERVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Advanced filters toggle */}
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`relative flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-[#4057fe] text-white border-[#4057fe]' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:bg-gray-50'}`}
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>
              )}
            </button>

            {/* Refresh */}
            <button
              onClick={() => { fetchLogs(currentPage, appliedFilters); fetchStats(); }}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Advanced filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button onClick={clearFilters} className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Clear all
              </button>
              <button onClick={applyFilters} className="text-sm px-4 py-2 rounded-lg bg-[#4057fe] text-white hover:bg-[#3347e0]">
                Apply filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-500">
            <ExclamationCircleIcon className="w-10 h-10" />
            <p className="text-sm font-medium">{error}</p>
            <button onClick={() => fetchLogs(currentPage, appliedFilters)} className="text-sm underline">Retry</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#e9f1ff] dark:bg-gray-700/60">
                  <tr>
                    {['Recipient Email', 'Child / Vaccine', 'Interval', 'Status', 'Appointment Date', 'Sent At', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        {Array.from({ length: 7 }).map((__, j) => (
                          <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" /></td>
                        ))}
                      </tr>
                    ))
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-16 text-gray-400 dark:text-gray-500">
                        <EnvelopeIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No notification logs found</p>
                      </td>
                    </tr>
                  ) : logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{log.recipientEmail ?? '—'}</div>
                        <div className="text-xs text-gray-400">{log.parentName}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-white">{log.childName ?? '—'}</div>
                        <div className="text-xs text-gray-400">{log.vaccineName}</div>
                      </td>
                      <td className="px-4 py-3"><IntervalBadge type={log.intervalType} /></td>
                      <td className="px-4 py-3"><StatusBadge status={log.status} /></td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">
                        {formatScheduledDate(log.scheduledDate)}
                        {log.scheduledTime && <div className="text-xs text-gray-400">{log.scheduledTime}</div>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300">{formatDate(log.sentAt)}</td>
                      <td className="px-4 py-3">
                        {log.status === 'failed' && log.errorMessage && (
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="text-xs text-red-500 hover:text-red-700 underline whitespace-nowrap"
                          >
                            View error
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                    const page = pagination.totalPages <= 7 ? i + 1 : currentPage <= 4 ? i + 1 : currentPage + i - 3;
                    if (page < 1 || page > pagination.totalPages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-xs rounded border transition-colors ${page === currentPage ? 'bg-[#4057fe] text-white border-[#4057fe]' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    disabled={currentPage === pagination.totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Error detail modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Delivery Error</h3>
              <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Recipient</span>
                <span className="font-medium text-gray-900 dark:text-white">{selectedLog.recipientEmail ?? '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Interval</span>
                <IntervalBadge type={selectedLog.intervalType} />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Attempted at</span>
                <span className="text-gray-700 dark:text-gray-300">{formatDate(selectedLog.sentAt)}</span>
              </div>
              <div>
                <span className="text-gray-500 block mb-1">Error message</span>
                <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded-lg whitespace-pre-wrap break-all border border-red-100 dark:border-red-800">
                  {selectedLog.errorMessage}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
